import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import { useAuth } from '../../context/AuthContext';
import { normalizeEmail } from '../../utils/clients';
import { carePlanIsDiscounted, daysUntilExpiry, formatMoney, formatMonthly, formatQuoteDate, formatStatus, isExpired, respondToQuote, STATUS_LABELS } from '../../utils/quotes';
import { CARE_TIER_LABELS, PAYMENT_NOTES } from '../../constants/pricing';
import { downloadSignedQuote } from '../../utils/quotePdf';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

// A pending serverTimestamp reads back as null, which would sort a brand-new
// quote to the wrong end of the list until the server confirmed it.
const ESTIMATE = { serverTimestamps: 'estimate' };

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  badge: ds.badge,
  error: ds.error,
  disabled: ds.disabled,
  // Sits beside Decline in a flex row, so it flexes rather than filling.
  accept: { ...ds.primaryButton, width: 'auto', flex: 1 },
  totalValue: ds.totalValue,
  empty: { ...ds.empty, margin: 0 },
  quote: {
    padding: '20px 0 0',
    marginTop: 20,
    borderTop: '1px solid #ececE6',
  },
  quoteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  quoteTitle: {
    fontFamily: MONO,
    fontSize: '0.95rem',
    fontWeight: 700,
  },
  quoteDate: {
    color: '#888',
    fontSize: '0.75rem',
    marginTop: 2,
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    fontSize: '0.88rem',
    color: '#333',
  },
  itemRowWithFeatures: {
    paddingBottom: 2,
  },
  itemAmount: {
    fontFamily: MONO,
    flexShrink: 0,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '12px 0 0',
    marginTop: 8,
    borderTop: '1px solid #ececE6',
    fontFamily: MONO,
  },
  // Indentation alone, the way a printed quote does it. A left rule sat flush
  // with the parent's own left edge, which read as a blockquote beside the line
  // rather than as detail underneath it.
  featureList: {
    listStyle: 'none',
    margin: '0 0 14px',
    padding: '0 0 0 18px',
  },
  // Flex rather than a list marker, so a feature long enough to wrap hangs
  // under its own text instead of running back under the dash.
  featureItem: {
    display: 'flex',
    gap: 8,
    fontSize: '0.78rem',
    color: '#999',
    lineHeight: 1.65,
  },
  featureMarker: {
    color: '#c4c4be',
    flexShrink: 0,
  },
  subtotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: '0.85rem',
    color: '#777',
  },
  discountRow: {
    color: '#1a7f37',
    fontWeight: 600,
  },
  strike: {
    textDecoration: 'line-through',
    color: '#aaa',
    fontWeight: 400,
    marginRight: 8,
  },
  savingBanner: {
    marginTop: 14,
    padding: '10px 14px',
    background: '#e9f7ef',
    border: '1px solid #c3e6d0',
    borderRadius: 10,
    color: '#1a7f37',
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'center',
  },
  notes: {
    fontSize: '0.85rem',
    color: '#777',
    lineHeight: 1.6,
    margin: '16px 0 0',
    padding: 12,
    background: '#f9f9f7',
    borderRadius: 8,
  },
  termsBox: {
    marginTop: 16,
    padding: '14px 16px',
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  termsLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#888',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  termsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '5px 0',
    fontSize: '0.86rem',
    color: '#333',
  },
  termsAmount: {
    fontFamily: MONO,
    flexShrink: 0,
  },
  termsNote: {
    fontSize: '0.75rem',
    color: '#999',
    lineHeight: 1.5,
    margin: '10px 0 0',
  },
  // Dimmed rather than hidden: opacity carries "inactive" without the client
  // wondering where a quote they remember has gone.
  quoteInactive: {
    opacity: 0.62,
  },
  withdrawn: {
    fontSize: '0.85rem',
    color: '#666',
    lineHeight: 1.55,
    marginTop: 20,
    padding: 14,
    background: '#f4f4f0',
    border: '1px solid #e4e4dd',
    borderRadius: 10,
  },
  expiringSoon: {
    color: '#92400e',
    fontWeight: 600,
  },
  expired: {
    fontSize: '0.85rem',
    color: '#8c2f22',
    lineHeight: 1.55,
    marginTop: 20,
    padding: 14,
    background: '#fdf0ee',
    border: '1px solid #f0c9c3',
    borderRadius: 10,
  },
  assurance: {
    fontSize: '0.78rem',
    color: '#888',
    lineHeight: 1.55,
    margin: '18px 0 0',
  },
  contractBox: {
    marginTop: 16,
    padding: 16,
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  contractLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#888',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  contractScrollWrap: {
    position: 'relative',
  },
  contractText: {
    fontSize: '0.82rem',
    color: '#333',
    lineHeight: 1.65,
    // Long contracts scroll inside the card rather than pushing the accept
    // buttons off-screen; whiteSpace keeps the admin's paragraph breaks.
    whiteSpace: 'pre-wrap',
    maxHeight: 320,
    overflowY: 'auto',
  },
  // Sits over the bottom of the scroll area while there's more to read, so a
  // long contract can't be mistaken for one that simply ends at the fold.
  // Matches signBox's background so it reads as the text fading out.
  contractFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 48,
    background: 'linear-gradient(to bottom, rgba(249,249,247,0), rgba(249,249,247,1))',
    pointerEvents: 'none',
  },
  pastToggle: {
    width: '100%',
    marginTop: 20,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  scrollHint: {
    fontSize: '0.78rem',
    color: '#92400e',
    margin: '10px 0 0',
  },
  download: {
    display: 'inline-block',
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#333',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  contractLink: {
    display: 'inline-block',
    marginTop: 16,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#333',
    fontSize: '0.85rem',
    textDecoration: 'none',
  },
  signBox: {
    marginTop: 16,
    padding: 16,
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: '0.85rem',
    color: '#333',
    lineHeight: 1.5,
    cursor: 'pointer',
  },
  signInput: {
    width: '100%',
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  actions: {
    display: 'flex',
    gap: 8,
    marginTop: 20,
  },
  decline: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#555',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
  responded: {
    fontSize: '0.85rem',
    color: '#777',
    marginTop: 20,
  },
  // Neutral rather than red: confirming an acceptance is a positive action,
  // so it shouldn't wear the same warning colours as declining or deleting.
  declineBox: {
    marginTop: 20,
    padding: 16,
    background: '#fdf0ee',
    border: '1px solid #f0c9c3',
    borderRadius: 10,
  },
  declineText: {
    fontSize: '0.85rem',
    color: '#8c2f22',
    lineHeight: 1.55,
    marginBottom: 14,
  },
  declineConfirm: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#c0392b',
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const BADGE_COLORS = ds.BADGE_COLORS;

function Quote({ quote }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  // Declining is as final as accepting — the rules only allow a response while
  // the quote is still 'sent' — so it gets the same deliberate second step.
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  // Only used when there's no contract — the tick-and-sign gate already makes
  // accepting deliberate, so a second step there would just be nagging.
  const contractRef = useRef(null);

  const expired = isExpired(quote);
  const daysLeft = daysUntilExpiry(quote);
  // Every acceptance is signed, contract or not. The quote itself carries the
  // scope, price and payment stages, so accepting it is agreeing to terms —
  // a name against those is worth far more than a bare click.
  const hasContract = !!(quote.contractText || quote.contractUrl);
  // Only gate on scrolling when there's wording on the page to scroll through.
  const mustRead = !!quote.contractText;
  const canTick = !mustRead || scrolledToEnd;
  const canAccept = agreed && signedName.trim().length > 0 && canTick;

  // Latches once they reach the bottom — scrolling back up doesn't undo it.
  const checkScrolledToEnd = useCallback(() => {
    const el = contractRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrolledToEnd(true);
  }, []);

  // A contract short enough not to overflow its box has nothing to scroll, so
  // it counts as read as soon as it renders.
  useEffect(() => {
    checkScrolledToEnd();
  }, [checkScrolledToEnd, quote.contractText]);

  const respond = async (status) => {
    setError('');
    setSubmitting(true);
    try {
      await respondToQuote(quote.id, status, signedName);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setSubmitting(false);
    }
  };

  // Withdrawn quotes stay readable — the client may want to see what was
  // offered — but are visibly inactive and carry no actions.
  const withdrawn = quote.status === 'withdrawn';

  return (
    <div style={{ ...styles.quote, ...(withdrawn ? styles.quoteInactive : {}) }}>
      <div style={styles.quoteHeader}>
        <div>
          <div style={styles.quoteTitle}>{quote.title}</div>
          {formatQuoteDate(quote.createdAt) && (
            <div style={styles.quoteDate}>Sent {formatQuoteDate(quote.createdAt)}</div>
          )}
          {quote.status === 'sent' && !expired && quote.expiresAt && (
            <div style={{ ...styles.quoteDate, ...(daysLeft <= 7 ? styles.expiringSoon : {}) }}>
              Valid until {formatQuoteDate(quote.expiresAt)}
              {daysLeft <= 7 && ` · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
            </div>
          )}
        </div>
        <span style={{ ...styles.badge, ...(BADGE_COLORS[quote.status] || {}) }}>
          {STATUS_LABELS[quote.status] || formatStatus(quote.status)}
        </span>
      </div>

      {(quote.lineItems || []).map((item, i) => (
        <div key={i}>
          {/* Less space below when features follow, so they read as part of
              this line rather than as something floating between two. */}
          <div style={{ ...styles.itemRow, ...((item.features || []).length ? styles.itemRowWithFeatures : {}) }}>
            <span>{item.description}</span>
            <span style={styles.itemAmount}>{formatMoney(item.amount)}</span>
          </div>
          {/* Features are priced at the parent line, so they carry no amount. */}
          {(item.features || []).length > 0 && (
            <ul style={styles.featureList}>
              {item.features.map((feature, fi) => (
                <li key={fi} style={styles.featureItem}>
                  <span style={styles.featureMarker}>–</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* Older quotes have no discount fields at all, hence the guard. */}
      {quote.discountPercent > 0 && (
        <>
          <div style={styles.subtotalRow}>
            <span>Subtotal</span>
            <span>{formatMoney(quote.subtotal)}</span>
          </div>
          <div style={{ ...styles.subtotalRow, ...styles.discountRow }}>
            <span>{quote.discountPercent}% discount</span>
            <span>−{formatMoney(quote.discountAmount)}</span>
          </div>
        </>
      )}

      <div style={styles.totalRow}>
        <span>Total</span>
        <span style={styles.totalValue}>
          {quote.discountPercent > 0 && (
            <span style={styles.strike}>{formatMoney(quote.subtotal)}</span>
          )}
          {formatMoney(quote.total)}
        </span>
      </div>

      {quote.discountPercent > 0 && (
        <div style={styles.savingBanner}>
          You're saving {formatMoney(quote.discountAmount)} on this quote.
        </div>
      )}

      {/* Older quotes predate these fields, so both are guarded. */}
      {quote.paymentSchedule?.length > 0 && (
        <div style={styles.termsBox}>
          <div style={styles.termsLabel}>How it's paid</div>
          {quote.paymentSchedule.map((stage, i) => (
            <div key={i} style={styles.termsRow}>
              <span>{stage.label}</span>
              <span style={styles.termsAmount}>{stage.percent}% · {formatMoney(stage.amount)}</span>
            </div>
          ))}
          {PAYMENT_NOTES.map((note, i) => (
            <p key={i} style={styles.termsNote}>{note}</p>
          ))}
        </div>
      )}

      {quote.carePlan && (
        <div style={styles.termsBox}>
          <div style={styles.termsLabel}>Care plan</div>
          <div style={styles.termsRow}>
            <span>{CARE_TIER_LABELS[quote.carePlan.tier] || quote.carePlan.tier}</span>
            <span style={styles.termsAmount}>
              {/* Undercutting our published rate is the discount — no separate
                  field, so any special price shows itself. */}
              {carePlanIsDiscounted(quote.carePlan) && (
                <span style={styles.strike}>{formatMonthly(quote.carePlan.listAmount)}</span>
              )}
              {formatMonthly(quote.carePlan.monthlyAmount)}
            </span>
          </div>
          <p style={styles.termsNote}>
            Billed monthly from launch, and separate from the total above. Cancel any time.
            {carePlanIsDiscounted(quote.carePlan) && quote.carePlan.discountMonths > 0 && (
              <>
                {' '}The reduced rate covers your first {quote.carePlan.discountMonths}{' '}
                month{quote.carePlan.discountMonths === 1 ? '' : 's'}, after which it's{' '}
                {formatMonthly(quote.carePlan.listAmount)} a month.
              </>
            )}
          </p>
        </div>
      )}

      {quote.notes && <p style={styles.notes}>{quote.notes}</p>}

      {quote.contractText && (
        <div style={styles.contractBox}>
          <div style={styles.contractLabel}>Contract</div>
          <div style={styles.contractScrollWrap}>
            <div ref={contractRef} onScroll={checkScrolledToEnd} style={styles.contractText}>
              {quote.contractText}
            </div>
            {!scrolledToEnd && <div style={styles.contractFade} />}
          </div>
        </div>
      )}

      {quote.contractUrl && (
        <a style={styles.contractLink} href={quote.contractUrl} target="_blank" rel="noopener noreferrer">
          📄 View original document
        </a>
      )}

      {withdrawn ? (
        <p style={styles.withdrawn}>
          This quote is no longer available — we've replaced or withdrawn it. Get in touch if you'd
          like it back on the table.
        </p>
      ) : expired ? (
        <p style={styles.expired}>
          This quote expired on {formatQuoteDate(quote.expiresAt)}. Ask us for an up-to-date one
          and we'll send a fresh quote over.
        </p>
      ) : quote.status === 'sent' ? (
        <>
          <div style={styles.signBox}>
            <label style={{ ...styles.checkboxRow, ...(canTick ? {} : styles.disabled) }}>
              <input
                type="checkbox"
                checked={agreed}
                disabled={!canTick}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>
                {hasContract
                  ? 'I have read and agree to the contract above.'
                  : 'I accept this quote, its price and its payment terms.'}
              </span>
            </label>
            {!canTick && (
              <p style={styles.scrollHint}>Scroll to the end of the contract to continue.</p>
            )}
            <input
              style={styles.signInput}
              type="text"
              placeholder="Type your full name to sign"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
            />
          </div>
          {/* Shown before the decision, not just after it: without this the
              client has no way of knowing they'll get a copy to keep. */}
          <p style={styles.assurance}>
            {hasContract
              ? 'Once you accept, you can download a signed PDF of this quote and its contract — with your name and the date on it — any time from this page.'
              : 'Once you accept, you can download a PDF of this quote any time from this page.'}
          </p>

          {confirmingDecline ? (
            <div style={styles.declineBox}>
              <div style={styles.declineText}>
                Decline this quote? We'll be told you're not going ahead, and it can't be
                accepted afterwards — just ask us if you'd like a fresh one.
              </div>
              <div style={{ ...styles.actions, marginTop: 0 }}>
                <button
                  type="button"
                  style={styles.declineConfirm}
                  onClick={() => respond('declined')}
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : 'Yes, decline'}
                </button>
                <button
                  type="button"
                  style={styles.decline}
                  onClick={() => setConfirmingDecline(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.actions}>
              {/* No separate confirm step: ticking and typing a name is already
                  a deliberate act, and a second panel would just be nagging. */}
              <button
                type="button"
                style={{ ...styles.accept, ...(canAccept ? {} : styles.disabled) }}
                onClick={() => respond('accepted')}
                disabled={submitting || !canAccept}
              >
                {submitting ? 'Saving…' : 'Accept & sign'}
              </button>
              <button
                type="button"
                style={styles.decline}
                onClick={() => setConfirmingDecline(true)}
                disabled={submitting}
              >
                Decline
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p style={styles.responded}>
            You {quote.status} this quote
            {formatQuoteDate(quote.respondedAt) && ` on ${formatQuoteDate(quote.respondedAt)}`}
            {quote.signedName && `, signed as ${quote.signedName}`}.
          </p>
          {quote.status === 'accepted' && (
            <button type="button" style={styles.download} onClick={() => downloadSignedQuote(quote)}>
              ⤓ Download signed copy (PDF)
            </button>
          )}
        </>
      )}

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

export default function QuotesCard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    if (!user?.email) return undefined;
    // Sorted in JS rather than with orderBy: combining a where() with an
    // orderBy() on a different field needs a composite index, and quote
    // counts per client are tiny.
    const q = query(collection(db, 'quotes'), where('clientEmail', '==', normalizeEmail(user.email)));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }));
        docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setQuotes(docs);
        setLoading(false);
      },
      (err) => {
        setError(err.message.replace('Firebase: ', ''));
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [user?.email]);

  // Quotes arrive newest-first, so the head is the current one and everything
  // behind it folds away — whatever its status, so the card shows exactly one
  // quote. Anything still unanswered back there is counted onto the toggle so
  // it isn't silently buried.
  const [latest, ...past] = quotes;
  const pendingInPast = past.filter((quote) => quote.status === 'sent').length;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Quotes</div>
      <p style={styles.description}>Quotes we've prepared for you.</p>

      {error && <div style={styles.error}>{error}</div>}
      {!error && loading && <p style={styles.empty}>Loading…</p>}
      {!error && !loading && quotes.length === 0 && (
        <p style={styles.empty}>No quotes yet — we'll let you know when one's ready.</p>
      )}

      {latest && <Quote key={latest.id} quote={latest} />}

      {past.length > 0 && (
        <>
          <button type="button" style={styles.pastToggle} onClick={() => setShowPast((prev) => !prev)}>
            {showPast ? 'Hide' : 'View'} past quotes ({past.length})
            {pendingInPast > 0 && ` · ${pendingInPast} awaiting your response`}
          </button>
          {showPast && past.map((quote) => <Quote key={quote.id} quote={quote} />)}
        </>
      )}
    </div>
  );
}
